import { useState, useMemo, useContext, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import AuthContext from '@/auth/application/AuthContext';
import { useGetDispatcherByUserIdQuery, ListDispatchersAutocompleteDocument } from '@/common/infrastructure/gql/generated/schema';
import { JOB_FORM_MODES, type JobFormMode } from '@/raptorjob/domain/constants/jobFormLabels';
import type { NamedEntity } from '@/raptorjob/application/utils/jobDataExtractor';

export const useAutoPopulatedDispatcher = (mode: JobFormMode) => {
  const authContext = useContext(AuthContext);
  const currentUserId = authContext?.user?.userId ? Number(authContext.user.userId) : null;

  const { data: dispatcherByUserIdData } = useGetDispatcherByUserIdQuery({
    variables: { userId: currentUserId! },
    skip: mode !== JOB_FORM_MODES.CREATE || !currentUserId || currentUserId <= 0,
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  });

  const shouldSearchFullList =
    mode === JOB_FORM_MODES.CREATE &&
    currentUserId &&
    currentUserId > 0 &&
    (!dispatcherByUserIdData?.dispatchers?.items?.[0] || dispatcherByUserIdData.dispatchers.items[0].user?.userId !== currentUserId);

  const [fetchDispatchersPage] = useLazyQuery(ListDispatchersAutocompleteDocument, {
    fetchPolicy: 'network-only'
  });

  const [foundDispatcherId, setFoundDispatcherId] = useState<number | null>(null);
  const [foundDispatcherName, setFoundDispatcherName] = useState<string | null>(null);
  const [hasSearchedAllPages, setHasSearchedAllPages] = useState(false);

  useEffect(() => {
    if (!shouldSearchFullList || hasSearchedAllPages || foundDispatcherId || !currentUserId) {
      return;
    }

    let isCancelled = false;

    const searchPage = async (skip: number) => {
      if (isCancelled) return;

      try {
        const result = await fetchDispatchersPage({
          variables: { skip, search: '' }
        });

        if (isCancelled) return;

        const items = result.data?.dispatchers?.items || [];
        const totalCount = result.data?.dispatchers?.totalCount || 0;

        const found = items.find((d: { user?: { userId?: number | null } | null }) => d.user?.userId === currentUserId);
        if (found) {
          setFoundDispatcherId(found.id ?? null);
          setFoundDispatcherName(found.name ?? null);
          return;
        }

        const nextSkip = skip + 100;
        if (nextSkip < totalCount) {
          setTimeout(() => searchPage(nextSkip), 100);
        } else {
          setHasSearchedAllPages(true);
        }
      } catch (error) {
        console.error('[useAutoPopulatedDispatcher] Error fetching dispatchers page:', error);
        setHasSearchedAllPages(true);
      }
    };

    searchPage(0);

    return () => {
      isCancelled = true;
    };
  }, [shouldSearchFullList, currentUserId, dispatcherByUserIdData]);

  useEffect(() => {
    if (!shouldSearchFullList) {
      setFoundDispatcherId(null);
      setFoundDispatcherName(null);
      setHasSearchedAllPages(false);
    }
  }, [shouldSearchFullList]);

  const autoPopulatedDispatcher = useMemo<NamedEntity | undefined>(() => {
    if (mode === JOB_FORM_MODES.CREATE && foundDispatcherId && foundDispatcherName) {
      return { id: foundDispatcherId, name: foundDispatcherName };
    }
    return undefined;
  }, [mode, foundDispatcherId, foundDispatcherName]);

  const autoPopulatedDispatcherId = useMemo<number | null>(() => {
    if (!currentUserId) return null;

    const directQueryDispatcher = dispatcherByUserIdData?.dispatchers?.items?.[0];
    if (directQueryDispatcher?.user?.userId === currentUserId) {
      return directQueryDispatcher.id ?? null;
    }

    if (foundDispatcherId) return foundDispatcherId;

    return null;
  }, [dispatcherByUserIdData?.dispatchers?.items, foundDispatcherId, currentUserId]);

  return {
    autoPopulatedDispatcher,
    autoPopulatedDispatcherId,
    dispatcherByUserIdData
  };
};
